import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Plus, Minus, Download, SearchIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
// --- ARTICLE DATA ---
const articles = [{
  id: 1,
  category: 'Compliance',
  title: 'TM44 compliance: what every building owner needs to know',
  date: '15 April 2026',
  href: '/knowledge-hub/compliance',
  image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80'
}, {
  id: 2,
  category: 'Compliance',
  title: 'F-Gas regulations explained for commercial properties',
  date: '10 April 2026',
  href: '/knowledge-hub/compliance',
  image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80'
}, {
  id: 3,
  category: 'Compliance',
  title: 'ISO 14001 and your HVAC system',
  date: '5 April 2026',
  href: '/knowledge-hub/compliance',
  image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80'
}, {
  id: 4,
  category: 'Compliance',
  title: 'Essential HVAC accreditations for UK businesses',
  date: '1 April 2026',
  href: '/knowledge-hub/compliance',
  image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80'
}, {
  id: 5,
  category: 'Maintenance',
  title: 'Commercial AC maintenance tips that extend system life',
  date: '28 March 2026',
  href: '/knowledge-hub/maintenance-guide',
  image: 'https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=800&q=80'
}, {
  id: 6,
  category: 'Maintenance',
  title: 'Understanding HVAC service contracts',
  date: '22 March 2026',
  href: '/knowledge-hub/maintenance-guide',
  image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80'
}, {
  id: 7,
  category: 'Maintenance',
  title: 'How to read your HVAC service report',
  date: '18 March 2026',
  href: '/knowledge-hub/maintenance-guide',
  image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&q=80'
}, {
  id: 8,
  category: 'Installation',
  title: 'Complete guide to commercial AC installation',
  date: '15 March 2026',
  href: '/knowledge-hub/installation-guide',
  image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80'
}, {
  id: 9,
  category: 'Installation',
  title: 'Daikin vs generic systems: why brand matters',
  date: '10 March 2026',
  href: '/knowledge-hub/installation-guide',
  image: 'https://images.unsplash.com/photo-1613545325278-f24b0cae1224?w=800&q=80'
}, {
  id: 10,
  category: 'Installation',
  title: 'Retail AC solutions: what shop owners need to know',
  date: '5 March 2026',
  href: '/knowledge-hub/installation-guide',
  image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80'
}, {
  id: 11,
  category: 'Repairs & efficiency',
  title: 'Warning signs your AC system needs repair',
  date: '1 March 2026',
  href: '/knowledge-hub/repairs-efficiency',
  image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&q=80'
}, {
  id: 12,
  category: 'Repairs & efficiency',
  title: 'Preventative maintenance vs reactive repairs',
  date: '25 February 2026',
  href: '/knowledge-hub/repairs-efficiency',
  image: 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=800&q=80'
}, {
  id: 13,
  category: 'Repairs & efficiency',
  title: 'Energy efficiency: how to cut your HVAC bills',
  date: '20 February 2026',
  href: '/knowledge-hub/repairs-efficiency',
  image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&q=80'
}, {
  id: 14,
  category: 'Eco & ventilation',
  title: 'UK ventilation requirements by sector',
  date: '15 February 2026',
  href: '/knowledge-hub/eco-ventilation',
  image: 'https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?w=800&q=80'
}, {
  id: 15,
  category: 'Eco & ventilation',
  title: 'Sustainable HVAC practices for 2026',
  date: '10 February 2026',
  href: '/knowledge-hub/eco-ventilation',
  image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800&q=80'
}];
const categories = ['Show all', 'Compliance', 'Maintenance', 'Installation', 'Repairs & efficiency', 'Eco & ventilation'];
// --- FAQ DATA ---
interface FAQ {
  q: string;
  a: string;
  cta: string;
  ctaHref: string;
}
interface FAQGroup {
  topic: string;
  items: FAQ[];
}
const faqGroups: FAQGroup[] = [{
  topic: 'Legal compliance',
  items: [{
    q: 'What is TM44?',
    a: 'An energy efficiency inspection required every 5 years for commercial AC systems totalling over 12kW in non-domestic buildings. Fines of up to £5,000 per system apply if missed.',
    cta: 'Read our compliance guide →',
    ctaHref: '/knowledge-hub/compliance'
  }, {
    q: "What's the difference between TM44 and F-Gas?",
    a: 'TM44 covers energy performance (every 5 years). F-Gas covers refrigerant leak checks (every 3–12 months by a REFCOM-registered engineer).',
    cta: 'Learn more about F-Gas →',
    ctaHref: '/knowledge-hub/compliance'
  }, {
    q: 'Do I need F-Gas certification?',
    a: 'Yes — both your company and individual engineers must be REFCOM-registered or hold City & Guilds qualifications to install or service refrigerant systems.',
    cta: 'Speak to an engineer →',
    ctaHref: '/#contact'
  }]
}, {
  topic: 'Maintenance',
  items: [{
    q: 'How often should I service my commercial AC?',
    a: 'Annually at minimum. We recommend every 12 months for small systems, 6 months for medium, and quarterly for large installations. Regular servicing is mandatory for warranty and compliance.',
    cta: 'Read our maintenance guide →',
    ctaHref: '/knowledge-hub/maintenance-guide'
  }, {
    q: 'What happens if a refrigerant leak is detected?',
    a: 'The leak must be repaired and re-checked within one month. All actions must be logged in your F-Gas records.',
    cta: 'Request a repair →',
    ctaHref: '/#contact'
  }]
}, {
  topic: 'Warranties',
  items: [{
    q: "What's a typical AC warranty?",
    a: 'Most manufacturers offer 3–10 years on parts (Daikin offers up to 10 years with annual servicing) and 1–2 years on labour.',
    cta: 'Learn about Daikin warranties →',
    ctaHref: '/knowledge-hub/installation-guide'
  }, {
    q: 'Can I lose my warranty?',
    a: 'Yes — skipping scheduled services or failing to register the system within 30–60 days of installation can void your warranty.',
    cta: 'Book a service →',
    ctaHref: '/#contact'
  }]
}, {
  topic: 'Ventilation & regulations',
  items: [{
    q: 'How many air changes per hour are required?',
    a: 'Offices require 6 per hour; gyms and commercial kitchens require 12 per hour, as per CIBSE Guide B2.',
    cta: 'Read our ventilation guide →',
    ctaHref: '/knowledge-hub/eco-ventilation'
  }, {
    q: 'What about R22 refrigerant?',
    a: 'R22 has been banned. Systems must use low-GWP alternatives such as R32.',
    cta: 'Discuss system upgrades →',
    ctaHref: '/#contact'
  }]
}, {
  topic: 'Costs & services',
  items: [{
    q: "What's your starting price?",
    a: 'Commercial AC servicing starts from £100 + VAT.',
    cta: 'Get a free quote →',
    ctaHref: '/#contact'
  }, {
    q: 'Why choose AccuTemp?',
    a: 'Our in-house engineers resolve issues directly — no 4–6 week waits for manufacturer callouts. Our installations consistently achieve 16-year lifespans.',
    cta: 'Read our installation guide →',
    ctaHref: '/knowledge-hub/installation-guide'
  }]
}];
export function KnowledgeHub() {
  const [activeCategory, setActiveCategory] = useState('Show all');
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredArticles = articles.filter(a => {
    const matchesCategory = activeCategory === 'Show all' || a.category === activeCategory;
    const matchesSearch = searchQuery.trim() === '' || a.title.toLowerCase().includes(searchQuery.toLowerCase()) || a.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  // --- SEO / JSON-LD ---
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqGroups.flatMap(g => g.items).map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://accutemp.co.uk/'
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Knowledge Hub',
      item: 'https://accutemp.co.uk/knowledge-hub'
    }]
  };
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Expert HVAC insights for commercial buildings',
    description: 'AccuTemp is the regional authority on commercial air conditioning. Explore our comprehensive guides covering everything from TM44 compliance and F-Gas regulations to maintenance strategies and energy efficiency.'
  };
  return <main className="min-h-screen bg-[#E8F0F8] font-sans text-primary-dark" data-id="element-528">
      {/* Inject JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-529" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(breadcrumbSchema)
    }} data-id="element-530" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(collectionSchema)
    }} data-id="element-531" />

      {/* Header & Articles Section */}
      <section className="pt-32 pb-24" data-id="element-532">
        <div className="container mx-auto px-6" data-id="element-533">
          {/* Header Layout (Sparc style) */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start mb-16" data-id="element-534">
            <div className="lg:col-span-7" data-id="element-535">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/40 mb-6 block" data-id="element-536">
                Resources
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-5xl md:text-6xl lg:text-7xl font-serif text-primary-dark leading-[1.1] tracking-tight" data-id="element-537">
                Expert HVAC insights for commercial buildings.
              </motion.h1>
            </div>
            <div className="lg:col-span-5 lg:pt-14" data-id="element-538">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-539">
                AccuTemp is the regional authority on commercial air
                conditioning. Explore our comprehensive guides covering
                everything from TM44 compliance and F-Gas regulations to
                maintenance strategies and energy efficiency.
              </motion.p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-xl mb-8" data-id="element-540">
            <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-dark/40" data-id="element-541" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search articles..." className="w-full bg-white rounded-full py-4 pl-14 pr-6 text-base text-primary-dark placeholder:text-primary-dark/40 focus:outline-none focus:ring-2 focus:ring-accent/50 border border-primary-dark/10 shadow-sm" data-id="element-542" />
          </div>

          {/* Filter Tabs */}
          <nav aria-label="Article categories" className="flex flex-wrap gap-2 mb-12 bg-primary-dark/5 p-1.5 rounded-full w-fit" data-id="element-543">
            {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${activeCategory === category ? 'bg-primary-dark text-white' : 'text-primary-dark/70 hover:text-primary-dark hover:bg-white/50'}`} data-id="element-544">
                {category}
              </button>)}
          </nav>

          {/* Article Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12" data-id="element-545">
            <AnimatePresence mode="popLayout" data-id="element-546">
              {filteredArticles.map((article, index) => <motion.article key={article.id} layout initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="group flex flex-col h-full" data-id="element-547">
                  <Link to={article.href} className="block overflow-hidden rounded-2xl aspect-[4/3] mb-6" data-id="element-548">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-549" />
                  </Link>
                  <div className="text-xs font-medium uppercase tracking-wider text-primary-dark/50 mb-3" data-id="element-550">
                    {article.category}
                  </div>
                  <Link to={article.href} className="mb-4" data-id="element-551">
                    <h2 className="text-xl md:text-2xl font-medium text-primary-dark line-clamp-2 group-hover:text-accent transition-colors" data-id="element-552">
                      {article.title}
                    </h2>
                  </Link>
                  <div className="text-sm text-primary-dark/50 mt-auto" data-id="element-553">
                    Written on {article.date}
                  </div>
                </motion.article>)}
            </AnimatePresence>
          </div>

          {/* No results */}
          {filteredArticles.length === 0 && <div className="text-center py-16" data-id="element-554">
              <p className="text-primary-dark/50 text-lg" data-id="element-555">
                No articles found matching your search.
              </p>
            </div>}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-24" data-id="element-556">
        <div className="container mx-auto px-6" data-id="element-557">
          <h2 className="text-3xl md:text-4xl font-serif text-primary-dark mb-16 text-center" data-id="element-558">
            Frequently asked questions
          </h2>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12" data-id="element-559">
            {faqGroups.map((group, groupIndex) => <div key={groupIndex} data-id="element-560">
                <h3 className="text-lg font-medium text-primary-dark/50 uppercase tracking-wider mb-6" data-id="element-561">
                  {group.topic}
                </h3>
                <div className="space-y-4" data-id="element-562">
                  {group.items.map((faq, faqIndex) => {
                const id = `${groupIndex}-${faqIndex}`;
                const isOpen = openFaqId === id;
                return <div key={id} className="border border-primary-dark/10 rounded-2xl overflow-hidden" data-id="element-563">
                        <button onClick={() => setOpenFaqId(isOpen ? null : id)} className="w-full px-6 py-5 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-564">
                          <span className="font-medium text-primary-dark pr-8" data-id="element-565">
                            {faq.q}
                          </span>
                          <span className="flex-shrink-0 text-primary-dark/40" data-id="element-566">
                            {isOpen ? <Minus className="w-5 h-5" data-id="element-567" /> : <Plus className="w-5 h-5" data-id="element-568" />}
                          </span>
                        </button>
                        <AnimatePresence data-id="element-569">
                          {isOpen && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.3
                    }} data-id="element-570">
                              <div className="px-6 pb-6 pt-2 text-primary-dark/70 leading-relaxed border-t border-primary-dark/5" data-id="element-571">
                                <p className="mb-4" data-id="element-572">{faq.a}</p>
                                {faq.ctaHref.startsWith('/') ? <Link to={faq.ctaHref} className="inline-flex items-center text-sm font-medium text-accent hover:text-primary-dark transition-colors" data-id="element-573">
                                    {faq.cta}
                                  </Link> : <a href={faq.ctaHref} className="inline-flex items-center text-sm font-medium text-accent hover:text-primary-dark transition-colors" data-id="element-574">
                                    {faq.cta}
                                  </a>}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
              })}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Gated Download Section */}
      <section className="bg-primary-dark py-24" data-id="element-575">
        <div className="container mx-auto px-6" data-id="element-576">
          <div className="max-w-5xl mx-auto" data-id="element-577">
            <div className="grid md:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-578">
              <div data-id="element-579">
                <h2 className="text-3xl md:text-4xl font-serif text-white mb-6 leading-tight" data-id="element-580">
                  Download our free TM44 compliance checklist
                </h2>
                <p className="text-white/70 leading-relaxed text-lg" data-id="element-581">
                  Ensure your commercial building meets all legal requirements.
                  Our comprehensive checklist covers everything from the 12kW
                  threshold to ISO 14001 integration and avoiding fines.
                </p>
              </div>
              <div className="bg-white/5 p-8 rounded-3xl border border-white/10 backdrop-blur-sm" data-id="element-582">
                <form className="space-y-4" onSubmit={e => e.preventDefault()} data-id="element-583">
                  <div className="relative" data-id="element-584">
                    <label htmlFor="download-email" className="sr-only" data-id="element-585">
                      Business email address
                    </label>
                    <input id="download-email" type="email" placeholder="Enter your business email address..." required className="w-full bg-white rounded-full py-4 pl-6 pr-6 text-sm focus:outline-none focus:ring-2 focus:ring-accent border-none text-primary-dark" data-id="element-586" />
                  </div>
                  <button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white py-3.5 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-2" data-id="element-587">
                    <Download className="w-4 h-4" data-id="element-588" />
                    Download free checklist
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-[#D4DDE8] py-20" data-id="element-589">
        <div className="container mx-auto px-6 text-center" data-id="element-590">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-591">
            <h2 className="text-3xl md:text-4xl font-serif text-primary-dark mb-6" data-id="element-592">
              Need help? Get a free HVAC audit
            </h2>
            <p className="text-primary-dark/70 mb-10 text-lg" data-id="element-593">
              Speak to our in-house engineers about your commercial air
              conditioning requirements, compliance needs, or maintenance
              contracts.
            </p>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-594">
              Request your audit
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-595" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}